'use client';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { routes } from '~constants/routes';
import { usePathname } from '~i18n/navigation';
import { VisibilityGuard } from '~shared/ui/molecules/VisibilityGuard';
import { SingleMediaPageDynamic } from '~widgets/SingleMediaPage/ui/SingleMediaPage';

import { MediaGalleryContext } from './index';
import type {
	MediaGalleryContextData,
	OpenMediaPageOptions,
	UnavailableMediaAction,
} from './types';

type Props = {
	children: React.ReactNode;
};

export type MediaPageHistoryState = {
	galleryPath?: string;
} | null;

const scrollToTop = () => {
	requestAnimationFrame(() => {
		requestAnimationFrame(() => {
			window.scrollTo({ top: 0 });
		});
	});
};

export const MediaGalleryContextProvider = ({ children }: Props) => {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const fullPath = `${pathname}?${searchParams.toString()}`;

	const firstRender = useRef(true);
	const [galleryPath, setGalleryPath] = useState<string>();
	const [unavailableMediaAction, setUnavailableMediaAction] =
		useState<UnavailableMediaAction>();

	// Can't use useParams because mediaId is not in route segments
	const mediaId = pathname.startsWith(routes.gallery.singleHome)
		? pathname.slice(pathname.lastIndexOf('/') + 1)
		: undefined;

	// Navigation
	const openMediaPage = useCallback(
		({
			mediaId,
			restoreScroll,
			unavailableMediaAction,
			replaceState,
		}: OpenMediaPageOptions) => {
			const lastHistoryState = history.state as MediaPageHistoryState;
			const lastGalleryPath = lastHistoryState?.galleryPath;

			const newHistoryState: MediaPageHistoryState = {
				galleryPath: lastGalleryPath ? lastGalleryPath : fullPath,
			};

			history[replaceState ? 'replaceState' : 'pushState'](
				newHistoryState,
				'',
				routes.gallery.single(mediaId)
			);

			setUnavailableMediaAction(unavailableMediaAction);

			if (restoreScroll) {
				scrollToTop();
			}
		},
		[fullPath]
	);

	useEffect(() => {
		if (firstRender.current) {
			// Duplicate render of Media Page is still possible in edge cases:
			// * Open Media Page from Gallery
			// * Reload the page
			// * Navigate to Gallery and back using browser buttons
			// History state should be cleared here, but clearing it causes full page reload on navigation.
			firstRender.current = false;
			return;
		}

		const historyState = history.state as MediaPageHistoryState;
		setGalleryPath(historyState?.galleryPath);
	}, [mediaId]); // "mediaId" is required to reload galleryPath

	// Arrow pagination
	const directNavigationMediaId = useRef(Number(mediaId));

	// Gallery data
	const [galleryData, setGalleryData] = useState<MediaGalleryContextData>();

	const value = useMemo(
		() => ({
			openMediaPage,
			galleryData,
			setGalleryData,
			directNavigationMediaId: directNavigationMediaId.current,
			galleryPath,
		}),
		[galleryData, openMediaPage, galleryPath]
	);

	const showMediaPageSlot = !!galleryPath && !!mediaId;

	return (
		<MediaGalleryContext.Provider value={value}>
			<VisibilityGuard
				slotContent={
					showMediaPageSlot && (
						<SingleMediaPageDynamic
							key={mediaId}
							mediaId={mediaId}
							updatePageTitle
							unavailableMediaAction={unavailableMediaAction}
						/>
					)
				}
				parallelRoutePathPrefix={routes.gallery.singleHome}
			>
				{children}
			</VisibilityGuard>
		</MediaGalleryContext.Provider>
	);
};
